import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Mail, Phone, MapPin } from 'lucide-react';
import { projectsAPI, equipmentAPI, crewAPI, crewAvailabilityAPI, customersAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { EquipmentSelector, CrewSelector, PriceSummary, AvailabilityWarningBanner } from './project';

const EditProjectDialog = ({ project, open, onOpenChange, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [availableEquipment, setAvailableEquipment] = useState([]);
  const [availableCrew, setAvailableCrew] = useState([]);
  const [availableCustomers, setAvailableCustomers] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [selectedCrew, setSelectedCrew] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [autoCalculate, setAutoCalculate] = useState(true);
  const [availabilityWarnings, setAvailabilityWarnings] = useState({});
  const [crewAvailabilityWarnings, setCrewAvailabilityWarnings] = useState({});
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [checkingCrewAvailability, setCheckingCrewAvailability] = useState(false);
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    name: '',
    customer: '',
    status: 'quote',
    startDate: '',
    endDate: '',
    location: '',
    totalValue: '',
    equipmentCount: '0',
    crewCount: '0',
    priority: 'medium',
    equipment: [],
    crew: [],
    autoInvoiceEnabled: false,
    autoInvoiceTime: '08:00'
  });

  useEffect(() => {
    if (open) {
      fetchEquipment();
      fetchCrew();
      fetchCustomers();
    }
  }, [open]);

  useEffect(() => {
    if (project && availableEquipment.length > 0) {
      let parsedEquipment = [];
      if (project.equipment && project.equipment.length > 0) {
        parsedEquipment = project.equipment.map(item => {
          if (typeof item === 'string') {
            const eq = availableEquipment.find(e => e.id === item);
            return { id: item, quantity: 1, maxQuantity: eq?.available || 10 };
          } else if (item.id) {
            const eq = availableEquipment.find(e => e.id === item.id);
            return { id: item.id, quantity: item.quantity || 1, maxQuantity: eq?.available || 10 };
          }
          return null;
        }).filter(Boolean);
      }
      
      setSelectedEquipment(parsedEquipment);
      setSelectedCrew(project.crew || []);
      
      // Find and set the customer
      if (project.customer && availableCustomers.length > 0) {
        const customer = availableCustomers.find(c => c.name === project.customer);
        if (customer) {
          setSelectedCustomer(customer);
        }
      }
      
      setFormData({
        name: project.name || '',
        customer: project.customer || '',
        status: project.status || 'quote',
        startDate: project.startDate || '',
        endDate: project.endDate || '',
        location: project.location || '',
        totalValue: project.totalValue?.toString() || '',
        equipmentCount: project.equipmentCount?.toString() || '0',
        crewCount: project.crewCount?.toString() || '0',
        priority: project.priority || 'medium',
        equipment: parsedEquipment,
        crew: project.crew || [],
        autoInvoiceEnabled: project.autoInvoiceEnabled || false,
        autoInvoiceTime: project.autoInvoiceTime || '08:00'
      });
      
      // Check availability after loading
      if (parsedEquipment.length > 0 && project.startDate && project.endDate) {
        setTimeout(() => checkAllEquipmentAvailability(parsedEquipment, project.startDate, project.endDate, project.id), 500);
      }
      // Check crew availability after loading
      if (project.crew && project.crew.length > 0 && project.startDate && project.endDate) {
        setTimeout(() => checkAllCrewAvailability(project.crew, project.startDate, project.endDate, project.id), 600);
      }
    }
  }, [project, availableEquipment, availableCustomers]);

  // Check availability when dates change
  useEffect(() => {
    if (formData.startDate && formData.endDate && selectedEquipment.length > 0 && project) {
      checkAllEquipmentAvailability(selectedEquipment, formData.startDate, formData.endDate, project.id);
    }
    if (formData.startDate && formData.endDate && selectedCrew.length > 0 && project) {
      checkAllCrewAvailability(selectedCrew, formData.startDate, formData.endDate, project.id);
    }
  }, [formData.startDate, formData.endDate]);

  // Auto-calculate total value
  useEffect(() => {
    if (!autoCalculate) return;
    
    const totalEquipmentValue = selectedEquipment.reduce((sum, item) => {
      const equipment = availableEquipment.find(eq => eq.id === item.id);
      return sum + ((equipment?.price || 0) * item.quantity);
    }, 0);
    
    const totalCrewValue = selectedCrew.reduce((sum, crewId) => {
      const crew = availableCrew.find(c => c.id === crewId);
      return sum + (crew?.ratePerDay || 0);
    }, 0);

    const totalDailyRate = totalEquipmentValue + totalCrewValue;
    
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      const days = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1);
      setFormData(prev => ({ ...prev, totalValue: (totalDailyRate * days).toFixed(2) }));
    } else if (totalDailyRate > 0) {
      setFormData(prev => ({ ...prev, totalValue: totalDailyRate.toFixed(2) }));
    }
  }, [selectedEquipment, selectedCrew, availableEquipment, availableCrew, formData.startDate, formData.endDate, autoCalculate]);

  const fetchEquipment = async () => {
    try {
      const res = await equipmentAPI.getAll();
      setAvailableEquipment(res.data.sort((a, b) => (a.serialNumber || '').localeCompare(b.serialNumber || '')));
    } catch (error) {
      console.error('Error fetching equipment:', error);
    }
  };

  const fetchCrew = async () => {
    try {
      const res = await crewAPI.getAll();
      setAvailableCrew(res.data);
    } catch (error) {
      console.error('Error fetching crew:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await customersAPI.getAll();
      setAvailableCustomers(res.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const handleCustomerSelect = (customerId) => {
    const customer = availableCustomers.find(c => c.id === customerId);
    if (customer) {
      setSelectedCustomer(customer);
      setFormData(prev => ({
        ...prev,
        customer: customer.name,
        location: customer.address || prev.location
      }));
    }
  };

  const checkEquipmentAvailability = async (equipmentId, quantity, excludeProjectId) => {
    if (!formData.startDate || !formData.endDate) return null;
    
    try {
      const response = await equipmentAPI.checkAvailability({
        equipment_id: equipmentId,
        start_date: formData.startDate,
        end_date: formData.endDate,
        quantity_needed: quantity,
        exclude_project_id: excludeProjectId
      });
      return response.data;
    } catch (error) {
      console.error('Error checking availability:', error);
      return null;
    }
  };

  const checkAllEquipmentAvailability = async (equipment, startDate, endDate, excludeProjectId) => {
    if (!startDate || !endDate || equipment.length === 0) return;
    
    setCheckingAvailability(true);
    const warnings = {};
    
    for (const item of equipment) {
      try {
        const response = await equipmentAPI.checkAvailability({
          equipment_id: item.id,
          start_date: startDate,
          end_date: endDate,
          quantity_needed: item.quantity,
          exclude_project_id: excludeProjectId
        });
        if (response.data) {
          warnings[item.id] = response.data;
        }
      } catch (error) {
        console.error('Error checking availability:', error);
      }
    }
    
    setAvailabilityWarnings(warnings);
    setCheckingAvailability(false);
  };

  const checkCrewAvailability = async (crewId, excludeProjectId) => {
    if (!formData.startDate || !formData.endDate) return null;
    
    try {
      const response = await crewAvailabilityAPI.checkAvailability({
        crew_id: crewId,
        start_date: formData.startDate,
        end_date: formData.endDate,
        exclude_project_id: excludeProjectId
      });
      return response.data;
    } catch (error) {
      console.error('Error checking crew availability:', error);
      return null;
    }
  };

  const checkAllCrewAvailability = async (crewIds, startDate, endDate, excludeProjectId) => {
    if (!startDate || !endDate || crewIds.length === 0) return;
    
    setCheckingCrewAvailability(true);
    const warnings = {};
    
    for (const crewId of crewIds) {
      try {
        const response = await crewAvailabilityAPI.checkAvailability({
          crew_id: crewId,
          start_date: startDate,
          end_date: endDate,
          exclude_project_id: excludeProjectId
        });
        if (response.data) {
          warnings[crewId] = response.data;
        }
      } catch (error) {
        console.error('Error checking crew availability:', error);
      }
    }
    
    setCrewAvailabilityWarnings(warnings);
    setCheckingCrewAvailability(false);
  };

  const handleAddEquipment = async (equipmentId) => {
    const existingIndex = selectedEquipment.findIndex(item => item.id === equipmentId);
    if (existingIndex === -1) {
      const equipment = availableEquipment.find(eq => eq.id === equipmentId);
      const maxQty = equipment?.available || 1;
      const newSelected = [...selectedEquipment, { id: equipmentId, quantity: 1, maxQuantity: maxQty }];
      setSelectedEquipment(newSelected);
      updateEquipmentFormData(newSelected);
      setAutoCalculate(true);
      
      if (formData.startDate && formData.endDate && project) {
        const result = await checkEquipmentAvailability(equipmentId, 1, project.id);
        if (result) {
          setAvailabilityWarnings(prev => ({ ...prev, [equipmentId]: result }));
        }
      }
    }
  };

  const handleRemoveEquipment = (equipmentId) => {
    const newSelected = selectedEquipment.filter(item => item.id !== equipmentId);
    setSelectedEquipment(newSelected);
    updateEquipmentFormData(newSelected);
    setAutoCalculate(true);
    setAvailabilityWarnings(prev => {
      const updated = { ...prev };
      delete updated[equipmentId];
      return updated;
    });
  };

  const handleQuantityChange = async (equipmentId, change) => {
    const newSelected = selectedEquipment.map(item => {
      if (item.id === equipmentId) {
        const newQty = Math.max(1, Math.min(item.maxQuantity, item.quantity + change));
        return { ...item, quantity: newQty };
      }
      return item;
    });
    setSelectedEquipment(newSelected);
    updateEquipmentFormData(newSelected);
    setAutoCalculate(true);
    
    const item = newSelected.find(i => i.id === equipmentId);
    if (item && formData.startDate && formData.endDate && project) {
      const result = await checkEquipmentAvailability(equipmentId, item.quantity, project.id);
      if (result) {
        setAvailabilityWarnings(prev => ({ ...prev, [equipmentId]: result }));
      }
    }
  };

  const updateEquipmentFormData = (equipment) => {
    const totalItems = equipment.reduce((sum, item) => sum + item.quantity, 0);
    setFormData(prev => ({ 
      ...prev, 
      equipment: equipment.map(e => ({ id: e.id, quantity: e.quantity })),
      equipmentCount: totalItems.toString()
    }));
  };

  const handleAddCrew = async (crewId) => {
    if (!selectedCrew.includes(crewId)) {
      const newSelected = [...selectedCrew, crewId];
      setSelectedCrew(newSelected);
      setFormData(prev => ({ ...prev, crew: newSelected, crewCount: newSelected.length.toString() }));
      setAutoCalculate(true);
      
      // Check availability for this crew member
      if (formData.startDate && formData.endDate && project) {
        const result = await checkCrewAvailability(crewId, project.id);
        if (result) {
          setCrewAvailabilityWarnings(prev => ({ ...prev, [crewId]: result }));
        }
      }
    }
  };

  const handleRemoveCrew = (crewId) => {
    const newSelected = selectedCrew.filter(id => id !== crewId);
    setSelectedCrew(newSelected);
    setFormData(prev => ({ ...prev, crew: newSelected, crewCount: newSelected.length.toString() }));
    setAutoCalculate(true);
    setCrewAvailabilityWarnings(prev => {
      const updated = { ...prev };
      delete updated[crewId];
      return updated;
    });
  };

  const handleManualValueChange = (value) => {
    setAutoCalculate(false);
    setFormData(prev => ({ ...prev, totalValue: value }));
  };

  const hasEquipmentAvailabilityIssues = Object.values(availabilityWarnings).some(w => !w.is_available);
  const hasCrewAvailabilityIssues = Object.values(crewAvailabilityWarnings).some(w => !w.is_available);
  const hasAvailabilityIssues = hasEquipmentAvailabilityIssues || hasCrewAvailabilityIssues;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (hasAvailabilityIssues) {
      const proceed = window.confirm(
        'Sommige apparatuur of personeelsleden hebben beschikbaarheidsconflicten. Wilt u toch doorgaan?'
      );
      if (!proceed) return;
    }
    
    setLoading(true);
    
    try {
      const totalItems = selectedEquipment.reduce((sum, item) => sum + item.quantity, 0);
      await projectsAPI.update(project.id, {
        ...formData,
        totalValue: parseFloat(formData.totalValue) || 0,
        equipmentCount: totalItems,
        crewCount: selectedCrew.length,
        equipment: selectedEquipment.map(e => ({ id: e.id, quantity: e.quantity })),
        crew: selectedCrew
      });
      
      toast({
        title: "Gelukt",
        description: "Project succesvol bijgewerkt"
      });
      
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast({
        title: "Fout",
        description: "Kan project niet bijwerken",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals for PriceSummary
  const equipmentDailyTotal = selectedEquipment.reduce((sum, item) => {
    const eq = availableEquipment.find(e => e.id === item.id);
    return sum + ((eq?.price || 0) * item.quantity);
  }, 0);

  const crewDailyTotal = selectedCrew.reduce((sum, crewId) => {
    const crew = availableCrew.find(c => c.id === crewId);
    return sum + (crew?.ratePerDay || 0);
  }, 0);

  const totalEquipmentItems = selectedEquipment.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Project Bewerken</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit-name">Projectnaam *</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Voer projectnaam in"
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-customer">Klant *</Label>
              <Select onValueChange={handleCustomerSelect} value={selectedCustomer?.id || ''}>
                <SelectTrigger data-testid="edit-customer-select-trigger">
                  <SelectValue placeholder={formData.customer || "Selecteer een klant"} />
                </SelectTrigger>
                <SelectContent>
                  {availableCustomers.map(customer => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.name} ({customer.type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Customer details display */}
            {selectedCustomer && (
              <div className="col-span-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="text-sm font-semibold text-blue-800 mb-2">Klantgegevens</h4>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-gray-700">
                    <Mail className="w-4 h-4 text-blue-500" />
                    <span>{selectedCustomer.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <Phone className="w-4 h-4 text-blue-500" />
                    <span>{selectedCustomer.phone}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <MapPin className="w-4 h-4 text-blue-500" />
                    <span>{selectedCustomer.address || 'Geen adres'}</span>
                  </div>
                </div>
              </div>
            )}
            
            <div>
              <Label htmlFor="edit-startDate">Startdatum *</Label>
              <Input
                id="edit-startDate"
                type="date"
                value={formData.startDate}
                onChange={(e) => { setFormData({ ...formData, startDate: e.target.value }); setAutoCalculate(true); }}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-endDate">Einddatum *</Label>
              <Input
                id="edit-endDate"
                type="date"
                value={formData.endDate}
                onChange={(e) => { setFormData({ ...formData, endDate: e.target.value }); setAutoCalculate(true); }}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-location">Locatie *</Label>
              <Input
                id="edit-location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Voer locatie in"
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-totalValue">
                Totale Waarde (€) 
                <span className={`text-xs ml-1 ${autoCalculate ? 'text-green-600' : 'text-orange-600'}`}>
                  ({autoCalculate ? 'Automatisch berekend' : 'Handmatig'})
                </span>
              </Label>
              <Input
                id="edit-totalValue"
                type="number"
                step="0.01"
                value={formData.totalValue}
                onChange={(e) => handleManualValueChange(e.target.value)}
                className={`font-semibold ${autoCalculate ? 'bg-green-50' : 'bg-orange-50'}`}
              />
            </div>
            <div>
              <Label htmlFor="edit-status">Status</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="quote">Offerte</SelectItem>
                  <SelectItem value="pending">In Afwachting</SelectItem>
                  <SelectItem value="confirmed">Bevestigd</SelectItem>
                  <SelectItem value="in-progress">In Uitvoering</SelectItem>
                  <SelectItem value="completed">Afgerond</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-priority">Prioriteit</Label>
              <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Laag</SelectItem>
                  <SelectItem value="medium">Gemiddeld</SelectItem>
                  <SelectItem value="high">Hoog</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
            <div className="col-span-2 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <Label className="text-base font-semibold">Automatische Facturatie</Label>
                  <p className="text-sm text-gray-600">Verstuur factuur automatisch na afloop huurperiode</p>
                </div>
                <input
                  type="checkbox"
                  id="edit-autoInvoiceEnabled"
                  checked={formData.autoInvoiceEnabled}
                  onChange={(e) => setFormData({ ...formData, autoInvoiceEnabled: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-300"
                />
              </div>
              {formData.autoInvoiceEnabled && (
                <div className="mt-3">
                  <Label htmlFor="edit-autoInvoiceTime">Verzendtijd</Label>
                  <input
                    type="time"
                    id="edit-autoInvoiceTime"
                    value={formData.autoInvoiceTime}
                    onChange={(e) => setFormData({ ...formData, autoInvoiceTime: e.target.value })}
                    className="w-32 px-3 py-2 border rounded-md"
                  />
                </div>
              )}
            </div>

          <EquipmentSelector
            availableEquipment={availableEquipment}
            selectedEquipment={selectedEquipment}
            availabilityWarnings={availabilityWarnings}
            checkingAvailability={checkingAvailability}
            hasDates={!!(formData.startDate && formData.endDate)}
            onAddEquipment={handleAddEquipment}
            onRemoveEquipment={handleRemoveEquipment}
            onQuantityChange={handleQuantityChange}
            testIdPrefix="edit-"
          />

          <CrewSelector
            availableCrew={availableCrew}
            selectedCrew={selectedCrew}
            availabilityWarnings={crewAvailabilityWarnings}
            checkingAvailability={checkingCrewAvailability}
            hasDates={!!(formData.startDate && formData.endDate)}
            onAddCrew={handleAddCrew}
            onRemoveCrew={handleRemoveCrew}
            testIdPrefix="edit-"
          />

          <PriceSummary
            equipmentDailyTotal={equipmentDailyTotal}
            crewDailyTotal={crewDailyTotal}
            totalEquipmentItems={totalEquipmentItems}
            crewCount={selectedCrew.length}
            startDate={formData.startDate}
            endDate={formData.endDate}
            totalValue={formData.totalValue}
          />

          <AvailabilityWarningBanner hasIssues={hasAvailabilityIssues} isEditMode={true} />
          
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuleren
            </Button>
            <Button 
              type="submit" 
              disabled={loading} 
              className={`${hasAvailabilityIssues ? 'bg-amber-500 hover:bg-amber-600' : 'bg-gradient-to-r from-purple-600 to-blue-600'}`}
            >
              {loading ? 'Bezig...' : hasAvailabilityIssues ? 'Toch Opslaan' : 'Wijzigingen Opslaan'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditProjectDialog;
